import {
  ESessionStorageKeys,
  FinancingConfigDto,
  HomeInformationInput,
  Loader,
  Merchant,
  StepperFormFooter,
  useMerchantById,
  useMerchantFinancingConfig,
} from '@applyonline/shared'
import { scrollToTop } from '@applyonline/shared/utils'
import { EApplicantType, EFinancingProgram, ELoanPurpose } from '@if/core/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { UpdatePrequalificationDto, useUpdatePrequalification } from '../../api/credit-api'
import { getRecaptchaToken } from '../../services/recaptcha'
import { Constants } from '../../types'
import { Applicant } from '../../types/Schemas/ApplicantSchema'
import { PersonalInformationInput, Prequalification } from '../../types/Schemas/PrequalificationSchema'
import AddressForm from './components/AddressForm'
import PersonalInformationForm from './components/PersonalInformationForm'
import {
  buildDefaultCoapplicant,
  buildDefaultPrequalification,
  getMonthlyIncome,
  useSessionStorage,
} from './submit-app-hooks'

const getDefaultPrequalInfo = (
  applicantType: EApplicantType,
  merchant: Merchant | null,
  urlMerchantId: string,
  financingConfig: FinancingConfigDto,
): Prequalification => {
  if (applicantType === EApplicantType.CoApplicant) {
    return buildDefaultCoapplicant(financingConfig)
  }
  return merchant
    ? buildDefaultPrequalification(merchant, financingConfig)
    : buildDefaultPrequalification({ id: urlMerchantId } as Merchant, financingConfig)
}

const UpdatePersonalInformationForm = () => {
  const { t } = useTranslation()
  const urlParams = useParams()
  const currentStep = urlParams.currentStep!
  const urlMerchantId = urlParams.merchantId!
  const { id, applicantType } = useParams<{ id: string; applicantType: EApplicantType }>()
  const [merchant, isFetchingMerchant] = useMerchantById(urlMerchantId, EFinancingProgram.Personal)
  const [merchantFinancingConfig, isFetchingConfig] = useMerchantFinancingConfig(
    urlMerchantId,
    EFinancingProgram.Personal,
  )

  const financingConfig = useMemo(() => {
    if (!merchant || !merchantFinancingConfig) return null

    const isMedicalCategory = [ELoanPurpose.Medical, ELoanPurpose.Aesthetics, ELoanPurpose.Dentistry].includes(
      merchant.serviceCategory,
    )
    const defaultMaxLoanAmount = isMedicalCategory ? Constants.MaxLoanAmountMedicard : Constants.MaxLoanAmount

    return {
      ...merchantFinancingConfig,
      maximumLoanAmount: [Constants.MaxLoanAmount, Constants.MaxLoanAmountMedicard].includes(
        merchantFinancingConfig.maximumLoanAmount,
      )
        ? defaultMaxLoanAmount
        : merchantFinancingConfig.maximumLoanAmount,
      minimumLoanAmount: merchantFinancingConfig.minimumLoanAmount ?? Constants.MinLoanAmount,
      maxLoanTerm: merchantFinancingConfig.maxLoanTerm ?? Constants.BaseMaxTerm,
    }
  }, [merchant, merchantFinancingConfig])

  const [prequalInfo, setPrequalInfo] = useSessionStorage<Prequalification>(
    applicantType === EApplicantType.CoApplicant
      ? ESessionStorageKeys.COAPPLICANT_INFO
      : ESessionStorageKeys.PREQUAL_INFO,
    financingConfig
      ? getDefaultPrequalInfo(applicantType ?? EApplicantType.Applicant, merchant, urlMerchantId, financingConfig)
      : ({} as Prequalification),
  )

  useEffect(() => {
    if (financingConfig && !prequalInfo.applicant) {
      setPrequalInfo(
        getDefaultPrequalInfo(applicantType ?? EApplicantType.Applicant, merchant, urlMerchantId, financingConfig),
      )
    }
  }, [financingConfig, prequalInfo.applicant, setPrequalInfo, applicantType, merchant, urlMerchantId])

  const [updatePrequal, isUpdating] = useUpdatePrequalification()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const navigate = useNavigate()
  const [disableNext, setDisableNext] = useState(false)

  const steps = useMemo(() => ['personal-information', 'address'], [])

  const cls = `form-section step-${currentStep}-active`

  const applicationURL = `/creditapplication/${id}`
  const updateApplicantInfoURL = `/${urlMerchantId}/creditapplication/${id}/editPersonalInformation/${applicantType}`

  const gotoStep = useCallback((stepPath: string) => {
    scrollToTop()
    navigate(`${updateApplicantInfoURL}/${stepPath}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePrequalificationChanged = (data: PersonalInformationInput) => {
    const { applicant, ...otherData } = data
    const updatedPrequalInfo = {
      ...prequalInfo,
      ...otherData,
      applicant: { ...prequalInfo.applicant, ...applicant },
    } as Prequalification
    setPrequalInfo(updatedPrequalInfo)
  }

  const handleApplicantChanged = (data: Partial<Applicant>) => {
    const update = { ...prequalInfo, applicant: { ...prequalInfo.applicant, ...data } }
    setPrequalInfo(update)
  }

  const handleSubmitComplete = useCallback(async () => {
    const recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_personalInformation')
    const dto: UpdatePrequalificationDto = {
      creditApplicationId: urlParams.id!,
      personalInfoDto: {
        ...prequalInfo,
      },
      recaptchaToken,
    }
    await updatePrequal(dto)
    navigate(`/creditapplication/${urlParams.id}`, { replace: true })
  }, [executeRecaptcha, urlParams.id, prequalInfo, updatePrequal, navigate])

  const nextStepAfterOrComplete = useCallback(async () => {
    const nextStepIdx = steps.indexOf(currentStep.toLowerCase()) + 1
    if (nextStepIdx < steps.length) {
      gotoStep(steps[nextStepIdx])
    } else {
      await handleSubmitComplete()
    }
  }, [currentStep, gotoStep, steps, handleSubmitComplete])

  const handleAddressUpdated = (data: HomeInformationInput) => {
    void handleApplicantChanged({
      currentAddress: data.currentAddress,
      spending: data.spending,
    } as Partial<Applicant>)
  }

  if (
    !urlMerchantId ||
    isFetchingMerchant ||
    isFetchingConfig ||
    isUpdating ||
    !merchant ||
    !financingConfig ||
    !prequalInfo.applicant
  )
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />

  return (
    <main className={cls}>
      <h1 className="heading-primary">{t('identity.updateInfo')}</h1>

      {currentStep === 'personal-information' && (
        <PersonalInformationForm
          isCoapplicant={applicantType === EApplicantType.CoApplicant}
          prequalificationData={prequalInfo}
          onPrequalificationChanged={handlePrequalificationChanged}
          nextStepAfter={nextStepAfterOrComplete}
          currentStep={currentStep}
          loanPurposeId={prequalInfo.loanPurposeId}
        />
      )}

      {currentStep === 'address' && (
        <AddressForm
          applicantData={prequalInfo.applicant}
          onApplicantChanged={handleAddressUpdated}
          nextStepAfter={nextStepAfterOrComplete}
          currentStep={currentStep}
          applicantName={prequalInfo.applicant.firstName}
          applicantBirthDate={prequalInfo.applicant.birthDate}
          monthlyIncome={getMonthlyIncome(prequalInfo.applicant)}
          setDisableNext={setDisableNext}
        />
      )}
      <StepperFormFooter
        currentStepIndex={steps.indexOf(currentStep)}
        steps={steps}
        disableBack={false}
        disableNext={disableNext}
        baseURL={steps.indexOf(currentStep) === 0 ? applicationURL : updateApplicantInfoURL}
      />
    </main>
  )
}

export default UpdatePersonalInformationForm
